import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Committee Member types (matching municipality app structure)
export interface AuditInfo {
  name: string;
  surname: string;
}

export interface CommitteeMember {
  id: string;
  name: string;
  surname: string;
  email?: string;
  profileImage?: string;
  translations?: Record<string, { name?: string; surname?: string }>;
  municipalityId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: AuditInfo;
  updatedBy?: AuditInfo;
}

interface CommitteeMemberListResponse {
  items: CommitteeMember[];
  total: number;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to CommitteeMember
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): CommitteeMember => {
  const docData = data as Record<string, unknown>;
  
  const createdByData = docData.createdBy as Record<string, unknown> | undefined;
  const createdBy: AuditInfo = createdByData ? {
    name: (createdByData.name as string) || '',
    surname: (createdByData.surname as string) || '',
  } : {
    name: '',
    surname: '',
  };

  const updatedByData = docData.updatedBy as Record<string, unknown> | undefined;
  const updatedBy: AuditInfo | undefined = updatedByData ? {
    name: (updatedByData.name as string) || '',
    surname: (updatedByData.surname as string) || '',
  } : undefined;

  return {
    id,
    name: (docData.name as string) || '',
    surname: (docData.surname as string) || '',
    email: docData.email as string | undefined,
    profileImage: docData.profileImage as string | undefined,
    translations: docData.translations as Record<string, { name?: string; surname?: string }> | undefined,
    municipalityId,
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    createdBy,
    updatedBy,
  };
};

export const useMunicipalityCommitteeMembers = (municipalityId: string | null) => {
  const [data, setData] = useState<CommitteeMemberListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchCommitteeMembers = async () => {
      try {
        setLoading(true);
        setError(null);

        const committeeMembersCollection = collection(db, 'municipalities', municipalityId, 'committeeMembers');
        const snapshot = await getDocs(query(committeeMembersCollection, orderBy('createdAt', 'desc')));

        const items: CommitteeMember[] = [];
        snapshot.forEach((doc) => {
          const member = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(member);
        });

        setData({
          items,
          total: items.length,
        });
      } catch (err) {
        console.error('Error fetching municipality committee members:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality committee members');
      } finally {
        setLoading(false);
      }
    };

    fetchCommitteeMembers();
  }, [municipalityId]);

  return { data, loading, error };
};
